import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  exportBackup,
  previewRestore,
  restoreBackup,
  downloadBackupZip,
  readBackupFile,
  validateBackup,
  downloadRestoreReport,
  SECTION_LABELS,
  ALL_SECTIONS
} from '../services/backup';

/* ── toast ───────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      bottom: '1.5rem',
      right: '1.5rem',
      zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff',
      padding: '0.85rem 1.25rem',
      borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      fontSize: '0.88rem',
      animation: 'slideIn 0.3s ease'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{
        background: 'none',
        border: 'none',
        color: 'rgba(255,255,255,.7)',
        cursor: 'pointer',
        fontSize: '1rem',
        padding: 0,
        marginLeft: '0.5rem'
      }}>×</button>
    </div>
  );
}

/* ── step 1: mode selection ───────────────────────────────────────────── */

function ModeSelection({ onSelect }) {
  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.6rem',
        color: 'var(--forest)',
        marginBottom: '0.5rem',
        textAlign: 'center'
      }}>
        Säkerhetskopiering
      </h2>
      <p style={{
        color: 'var(--text-soft)',
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        Välj vad du vill göra
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem'
      }}>
        <button
          onClick={() => onSelect('backup')}
          style={{
            padding: '2rem',
            border: '2px solid var(--border)',
            borderRadius: 8,
            background: 'var(--white)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--forest)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💾</div>
          <h3 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.2rem',
            color: 'var(--forest)',
            marginBottom: '0.5rem'
          }}>
            Skapa backup
          </h3>
          <p style={{ color: 'var(--text-soft)', fontSize: '0.9rem' }}>
            Exportera data från databasen
          </p>
        </button>

        <button
          onClick={() => onSelect('restore')}
          style={{
            padding: '2rem',
            border: '2px solid var(--border)',
            borderRadius: 8,
            background: 'var(--white)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--danger)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(176,48,48,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📥</div>
          <h3 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.2rem',
            color: 'var(--danger)',
            marginBottom: '0.5rem'
          }}>
            Återställ
          </h3>
          <p style={{ color: 'var(--text-soft)', fontSize: '0.9rem' }}>
            Ladda upp backup-fil
          </p>
        </button>
      </div>
    </div>
  );
}

/* ── step 2a: backup selection ────────────────────────────────────────── */

function BackupSelection({ selectedSections, setSelectedSections, includeFiles, setIncludeFiles, onNext, onBack }) {
  const allSelected = selectedSections.length === ALL_SECTIONS.length;

  const toggleSection = (section) => {
    setSelectedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedSections([]);
    } else {
      setSelectedSections([...ALL_SECTIONS]);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.6rem',
        color: 'var(--forest)',
        marginBottom: '0.5rem'
      }}>
        Välj vad som ska säkerhetskopieras
      </h2>
      <p style={{ color: 'var(--text-soft)', marginBottom: '1.5rem' }}>
        Markera de sektioner du vill inkludera i backupen
      </p>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem',
          background: 'var(--cream)',
          borderRadius: 6,
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            style={{ width: 20, height: 20 }}
          />
          <span style={{ fontWeight: 600 }}>Markera alla</span>
        </label>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        marginBottom: '1.5rem'
      }}>
        {ALL_SECTIONS.map(section => (
          <label
            key={section}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              border: '1px solid var(--border)',
              borderRadius: 6,
              cursor: 'pointer',
              background: selectedSections.includes(section) ? 'rgba(44,74,46,0.05)' : 'var(--white)',
              transition: 'background 0.15s'
            }}
          >
            <input
              type="checkbox"
              checked={selectedSections.includes(section)}
              onChange={() => toggleSection(section)}
              style={{ width: 20, height: 20 }}
            />
            <span style={{ fontSize: '1.5rem' }}>{SECTION_LABELS[section].icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                {SECTION_LABELS[section].label}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
                {SECTION_LABELS[section].description}
              </div>
            </div>
          </label>
        ))}
      </div>

      <div style={{
        padding: '1rem',
        background: 'var(--cream)',
        borderRadius: 6,
        marginBottom: '1.5rem'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={includeFiles}
            onChange={(e) => setIncludeFiles(e.target.checked)}
            style={{ width: 20, height: 20 }}
          />
          <span>
            <strong>Inkludera uppladdade filer</strong> (PDF, DOC, DOCX)
            <br />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
              Ökar filstorleken avsevärt
            </span>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={onBack}
          className="btn btn-ghost"
          style={{ flex: 1 }}
        >
          Tillbaka
        </button>
        <button
          onClick={onNext}
          disabled={selectedSections.length === 0}
          className="btn btn-forest"
          style={{ flex: 2 }}
        >
          Skapa backup
        </button>
      </div>
    </div>
  );
}

/* ── step 2b: restore upload ─────────────────────────────────────────── */

function RestoreUpload({ onFileSelect, onNext, onBack }) {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    await processFile(droppedFile);
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    await processFile(selectedFile);
  };

  const processFile = async (selectedFile) => {
    setError(null);
    
    if (!selectedFile) {
      setError('Ingen fil vald');
      return;
    }
    
    if (!selectedFile.name.endsWith('.json')) {
      setError('Filen måste vara i JSON-format');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('Filen får inte vara större än 50 MB');
      return;
    }
    
    try {
      const backup = await readBackupFile(selectedFile);
      const validation = validateBackup(backup);
      
      if (!validation.valid) {
        setError('Ogiltig backup-fil: ' + validation.errors.join(', '));
        return;
      }
      
      setFile({ file: selectedFile, data: backup });
      onFileSelect(backup);
    } catch (err) {
      setError(err.message || 'Kunde inte läsa filen');
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.6rem',
        color: 'var(--danger)',
        marginBottom: '0.5rem'
      }}>
        Återställ från backup
      </h2>
      <p style={{ color: 'var(--text-soft)', marginBottom: '1.5rem' }}>
        Ladda upp en backup-fil från din dator
      </p>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--forest)' : 'var(--border)'}`,
          borderRadius: 8,
          padding: '3rem 2rem',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? 'rgba(44,74,46,0.05)' : 'var(--white)',
          transition: 'all 0.2s',
          marginBottom: '1.5rem'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📂</div>
        <p style={{ color: 'var(--text-mid)', marginBottom: '0.5rem' }}>
          <strong>Dra hit en fil</strong> eller klicka för att välja
        </p>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
          Format: .json (max 50 MB)
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          background: 'rgba(176,48,48,0.1)',
          border: '1px solid rgba(176,48,48,0.3)',
          borderRadius: 6,
          color: 'var(--danger)',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {file && (
        <div style={{
          padding: '1rem',
          background: 'rgba(44,74,46,0.1)',
          border: '1px solid rgba(44,74,46,0.3)',
          borderRadius: 6,
          marginBottom: '1rem'
        }}>
          <strong>Vald fil:</strong> {file.file.name}
          <br />
          <span style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
            {(file.file.size / 1024 / 1024).toFixed(2)} MB •{' '}
            {file.data.sections.length} sektioner
          </span>
        </div>
      )}

      <div style={{
        padding: '1rem',
        background: 'rgba(176,48,48,0.05)',
        border: '1px solid rgba(176,48,48,0.2)',
        borderRadius: 6,
        marginBottom: '1.5rem'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ width: 20, height: 20, marginTop: '0.15rem' }}
          />
          <span style={{ color: 'var(--danger)' }}>
            <strong>Jag förstår att återställning kommer att ersätta all befintlig data</strong>
            <br />
            <span style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
              Nuvarande innehåll kommer att tas bort och ersättas med innehållet från backup-filen.
              Detta kan inte ångras.
            </span>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={onBack}
          className="btn btn-ghost"
          style={{ flex: 1 }}
        >
          Tillbaka
        </button>
        <button
          onClick={() => onNext(file.data)}
          disabled={!file || !confirmed}
          className="btn btn-forest"
          style={{
            flex: 2,
            background: !file || !confirmed ? 'var(--text-soft)' : 'var(--danger)'
          }}
        >
          Visa förhandsgranskning
        </button>
      </div>
    </div>
  );
}

/* ── step 3: preview ─────────────────────────────────────────────────── */

function PreviewRestore({ preview, backup, selectedSections, onSectionsChange, onNext, onBack }) {
  const sections = Object.entries(preview.sections);
  const allSelected = sections.length > 0 && sections.every(([section]) => selectedSections.includes(section));

  const toggleSection = (section) => {
    onSectionsChange(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      onSectionsChange([]);
    } else {
      onSectionsChange(sections.map(([section]) => section));
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.6rem',
        color: 'var(--forest)',
        marginBottom: '0.5rem'
      }}>
        Förhandsgranskning
      </h2>
      <p style={{ color: 'var(--text-soft)', marginBottom: '1.5rem' }}>
        Granska ändringarna innan du återställer. Avmarkera sektioner du inte vill återställa.
      </p>

      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: '1.5rem'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--cream)' }}>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-soft)',
                borderBottom: '1px solid var(--border)',
                width: '40px'
              }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-soft)',
                borderBottom: '1px solid var(--border)'
              }}>
                Sektion
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-soft)',
                borderBottom: '1px solid var(--border)'
              }}>
                Nuvarande
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-soft)',
                borderBottom: '1px solid var(--border)'
              }}>
                Efter
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-soft)',
                borderBottom: '1px solid var(--border)'
              }}>
                Ändring
              </th>
            </tr>
          </thead>
          <tbody>
            {sections.map(([section, data]) => {
              const isSelected = selectedSections.includes(section);
              return (
                <tr key={section} style={{
                  borderBottom: '1px solid var(--border)',
                  opacity: isSelected ? 1 : 0.5,
                  background: isSelected ? 'transparent' : 'rgba(0,0,0,0.02)'
                }}>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSection(section)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{ marginRight: '0.5rem' }}>{SECTION_LABELS[section].icon}</span>
                    {SECTION_LABELS[section].label}
                    {!isSelected && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--text-soft)',
                        fontStyle: 'italic'
                      }}>
                        (hoppas över)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: 'var(--text-soft)' }}>
                    {data.current}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600 }}>
                    {isSelected ? data.after : '-'}
                  </td>
                  <td style={{
                    padding: '0.75rem 1rem',
                    textAlign: 'center',
                    color: !isSelected ? 'var(--text-soft)' : data.change > 0 ? 'var(--forest)' : data.change < 0 ? 'var(--danger)' : 'var(--text-soft)'
                  }}>
                    {!isSelected ? '-' : (data.change > 0 ? `+${data.change}` : data.change)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedSections.length === 0 && (
        <div style={{
          padding: '1rem',
          background: 'rgba(176,48,48,0.1)',
          border: '1px solid rgba(176,48,48,0.3)',
          borderRadius: 6,
          marginBottom: '1.5rem',
          color: 'var(--danger)'
        }}>
          ⚠️ Välj minst en sektion att återställa.
        </div>
      )}

      {backup.data.users && backup.data.users.length > 0 && (
        <div style={{
          padding: '1rem',
          background: 'rgba(176,140,48,0.1)',
          border: '1px solid rgba(176,140,48,0.3)',
          borderRadius: 6,
          marginBottom: '1.5rem'
        }}>
          <strong>⚠️ Viktigt om användare:</strong>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
            Återställda användare får tillfälliga lösenord och måste byta vid första inloggning.
            <br />
            Nuvarande användare (inklusive dig själv) påverkas inte.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={onBack}
          className="btn btn-ghost"
          style={{ flex: 1 }}
        >
          Tillbaka
        </button>
        <button
          onClick={onNext}
          disabled={selectedSections.length === 0}
          className="btn btn-forest"
          style={{
            flex: 2,
            background: 'var(--danger)',
            opacity: selectedSections.length === 0 ? 0.5 : 1,
            cursor: selectedSections.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          ⚠️ Återställ {selectedSections.length > 0 && `(${selectedSections.length} sektioner)`}
        </button>
      </div>
    </div>
  );
}

/* ── step 4: progress ────────────────────────────────────────────────── */

function ProgressRestore({ progress, onCancel }) {
  return (
    <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.6rem',
        color: 'var(--forest)',
        marginBottom: '2rem'
      }}>
        Återställer data...
      </h2>

      <div style={{
        width: '100%',
        height: 8,
        background: 'var(--cream)',
        borderRadius: 4,
        overflow: 'hidden',
        marginBottom: '2rem'
      }}>
        <div style={{
          width: `${progress.percent}%`,
          height: '100%',
          background: 'var(--forest)',
          transition: 'width 0.3s'
        }} />
      </div>

      <p style={{ color: 'var(--text-mid)', marginBottom: '0.5rem' }}>
        {progress.message}
      </p>

      <p style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
        Stäng inte fönstret under återställningen
      </p>

      <button
        onClick={onCancel}
        className="btn btn-ghost"
        style={{ marginTop: '2rem' }}
      >
        Avbryt
      </button>
    </div>
  );
}

/* ── step 5: complete ─────────────────────────────────────────────────── */

function CompleteRestore({ report, onDownload, onFinish }) {
  const totalRestored = Object.values(report.restored).reduce((a, b) => a + b, 0);
  const hasFailed = Object.keys(report.failed).length > 0;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>

      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.8rem',
        color: 'var(--forest)',
        marginBottom: '0.5rem'
      }}>
        Återställning klar!
      </h2>

      <p style={{ color: 'var(--text-mid)', marginBottom: '2rem' }}>
        {totalRestored} poster återställda från backup
      </p>

      {report.tempPasswords && report.tempPasswords.length > 0 && (
        <div style={{
          textAlign: 'left',
          padding: '1rem',
          background: 'rgba(176,140,48,0.1)',
          border: '1px solid rgba(176,140,48,0.3)',
          borderRadius: 6,
          marginBottom: '1.5rem'
        }}>
          <strong>Tillfälliga lösenord:</strong>
          <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 1rem' }}>
            Följande användare måste byta lösenord vid första inloggning:
          </p>
          <table style={{ width: '100%', fontSize: '0.85rem' }}>
            <tbody>
              {report.tempPasswords.map(({ username, tempPassword }) => (
                <tr key={username}>
                  <td style={{ padding: '0.25rem 0' }}>{username}</td>
                  <td style={{ fontFamily: 'monospace', color: 'var(--danger)' }}>
                    {tempPassword}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasFailed && (
        <div style={{
          textAlign: 'left',
          padding: '1rem',
          background: 'rgba(176,48,48,0.1)',
          border: '1px solid rgba(176,48,48,0.3)',
          borderRadius: 6,
          marginBottom: '1.5rem'
        }}>
          <strong>Varning:</strong> Vissa sektioner kunde inte återställas:
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
            {Object.entries(report.failed).map(([section, error]) => (
              <li key={section}>{SECTION_LABELS[section]?.label || section}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
        <button
          onClick={onDownload}
          className="btn btn-forest"
        >
          📥 Ladda ner rapport
        </button>
        <button
          onClick={onFinish}
          className="btn btn-ghost"
        >
          Tillbaka till dashboard
        </button>
      </div>
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function BackupRestoreManager() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null);
  const [selectedSections, setSelectedSections] = useState([...ALL_SECTIONS]);
  const [includeFiles, setIncludeFiles] = useState(false);
  const [backup, setBackup] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedRestoreSections, setSelectedRestoreSections] = useState([]);
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Step 1: Select mode
  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    setStep(2);
  };

  // Step 2: Backup - Create backup
  const handleCreateBackup = async () => {
    setLoading(true);
    try {
      const result = await exportBackup(selectedSections, includeFiles);
      setBackup(result.backup);
      downloadBackupZip(result.backup);
      showToast('Backup skapad och nedladdad');
      setStep(1); // Back to start
    } catch (err) {
      showToast(err.message || 'Kunde inte skapa backup', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Restore - File selected
  const handleFileSelect = (backupData) => {
    setBackup(backupData);
  };

  // Step 2: Restore - Preview
  const handlePreview = async (backupData) => {
    setLoading(true);
    try {
      const result = await previewRestore(backupData);
      setPreview(result.preview);
      // Initialize selected sections to all sections in the backup
      setSelectedRestoreSections(Object.keys(result.preview.sections));
      setStep(3);
    } catch (err) {
      showToast(err.message || 'Kunde inte visa förhandsgranskning', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Restore
  const handleRestore = async () => {
    if (selectedRestoreSections.length === 0) {
      showToast('Välj minst en sektion att återställa', 'error');
      return;
    }

    setStep(4);
    setProgress({ percent: 10, message: 'Validerar data...' });

    try {
      // Create filtered backup with only selected sections
      const filteredBackup = {
        ...backup,
        sections: selectedRestoreSections,
        data: {}
      };
      
      // Only include data for selected sections
      for (const section of selectedRestoreSections) {
        if (backup.data[section]) {
          filteredBackup.data[section] = backup.data[section];
        }
      }

      // Simulate progress steps
      const steps = [
        { percent: 25, message: 'Rensar befintlig data...', delay: 500 },
        { percent: 50, message: 'Återställer sektioner...', delay: 1000 },
        { percent: 75, message: 'Slutför återställning...', delay: 1000 },
        { percent: 90, message: 'Genererar rapport...', delay: 500 }
      ];

      for (const step of steps) {
        await new Promise(r => setTimeout(r, step.delay));
        setProgress({ percent: step.percent, message: step.message });
      }

      const result = await restoreBackup(filteredBackup, { sections: selectedRestoreSections });
      setReport(result.report);
      setProgress({ percent: 100, message: 'Klar!' });
      setStep(5);
    } catch (err) {
      setStep(3);
      showToast(err.message || 'Återställning misslyckades', 'error');
    }
  };

  // Download report
  const handleDownloadReport = () => {
    if (report) {
      downloadRestoreReport(report);
    }
  };

  // Finish
  const handleFinish = () => {
    setStep(1);
    setMode(null);
    setSelectedSections([...ALL_SECTIONS]);
    setIncludeFiles(false);
    setBackup(null);
    setPreview(null);
    setProgress({ percent: 0, message: '' });
    setReport(null);
  };

  // Go back
  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setMode(null);
    } else if (step === 3) {
      setStep(2);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1.5rem' }}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {loading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(255,255,255,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100
        }}>
          <div style={{ fontSize: '2rem' }}>Laddar...</div>
        </div>
      )}

      {/* Step indicator */}
      {mode && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          {[1, 2, 3, 4, 5].map(s => (
            <div
              key={s}
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: s <= step ? 'var(--forest)' : 'var(--border)'
              }}
            />
          ))}
        </div>
      )}

      {/* Step content */}
      {step === 1 && (
        <ModeSelection onSelect={handleModeSelect} />
      )}

      {step === 2 && mode === 'backup' && (
        <BackupSelection
          selectedSections={selectedSections}
          setSelectedSections={setSelectedSections}
          includeFiles={includeFiles}
          setIncludeFiles={setIncludeFiles}
          onNext={handleCreateBackup}
          onBack={handleBack}
        />
      )}

      {step === 2 && mode === 'restore' && (
        <RestoreUpload
          onFileSelect={handleFileSelect}
          onNext={handlePreview}
          onBack={handleBack}
        />
      )}

      {step === 3 && preview && (
        <PreviewRestore
          preview={preview}
          backup={backup}
          selectedSections={selectedRestoreSections}
          onSectionsChange={setSelectedRestoreSections}
          onNext={handleRestore}
          onBack={handleBack}
        />
      )}

      {step === 4 && (
        <ProgressRestore
          progress={progress}
          onCancel={() => setStep(3)}
        />
      )}

      {step === 5 && report && (
        <CompleteRestore
          report={report}
          onDownload={handleDownloadReport}
          onFinish={handleFinish}
        />
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
